import { ApiRecurringPaymentRuleService } from "@/infrastructure/services/ApiRecurringPaymentRuleService";
import { ErrorHandler, ERROR_CONTEXTS } from "@/shared/utils/errorHandler";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Use cases for recurring payment rules (subscriptions) management
 * Provides high-level operations for the dashboard
 */
export class RecurringPaymentRuleUseCases {
    constructor(service) {
        Object.defineProperty(this, "service", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.service = service || new ApiRecurringPaymentRuleService();
    }
    /**
     * Get a single recurring payment rule by ID
     * @param id - Rule ID (UUID)
     * @returns The recurring payment rule or null if not found
     */
    async getById(id) {
        try {
            logger.debug("Fetching recurring payment rule by ID", { id });
            const result = await this.service.getById(id);
            if (result) {
                logger.debug("Recurring payment rule fetched successfully", { id });
            }
            else {
                logger.debug("Recurring payment rule not found", { id });
            }
            return result;
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.DATA_FETCH);
            throw error;
        }
    }
    /**
     * Get a paginated list of recurring payment rules
     * @param page - Page number (1-based)
     * @param pageSize - Number of items per page
     * @param searchTerm - Optional search term for filtering by Rule ID, merchant reference, or shopper reference
     * @returns Paginated list of recurring payment rules
     */
    async getList(page, pageSize, searchTerm, filters) {
        try {
            logger.debug("Fetching recurring payment rules list", {
                page,
                pageSize,
                searchTerm,
                filters,
            });
            const result = await this.service.getList(page, pageSize, searchTerm, filters);
            logger.debug("Recurring payment rules list fetched successfully", {
                totalCount: result.totalCount,
                itemCount: result.items.length,
            });
            return result;
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.DATA_FETCH);
            throw error;
        }
    }
    /**
     * Get faceted filter counts for recurring payment rules
     * @param filters - Optional filters for contextual counting
     * @returns Filter counts per status and frequency
     */
    async getFilterCounts(filters) {
        try {
            logger.debug("Fetching filter counts", { filters });
            const result = await this.service.getFilterCounts(filters);
            logger.debug("Filter counts fetched successfully");
            return result;
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.DATA_FETCH);
            throw error;
        }
    }
    /**
     * Cancel a recurring payment rule (subscription)
     * @param id - Rule ID (UUID)
     * @param cancellationReason - Optional reason for cancellation
     * @returns Success response
     */
    async cancel(id, cancellationReason) {
        try {
            logger.debug("Cancelling recurring payment rule", { id, cancellationReason });
            const result = await this.service.cancel(id, cancellationReason);
            logger.debug("Recurring payment rule cancelled successfully", { id });
            return result;
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_CANCELLATION);
            throw error;
        }
    }
    /**
     * Pause an active recurring payment rule (temporarily stops future transactions)
     * @param id - Rule ID (UUID)
     * @returns Promise that resolves when paused
     */
    async pause(id) {
        try {
            logger.debug("Pausing recurring payment rule", { id });
            await this.service.pause(id);
            logger.debug("Recurring payment rule paused successfully", { id });
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_UPDATE);
            throw error;
        }
    }
    /**
     * Resume a paused recurring payment rule (restarts future transactions)
     * @param id - Rule ID (UUID)
     * @returns Promise that resolves when resumed
     */
    async resume(id) {
        try {
            logger.debug("Resuming recurring payment rule", { id });
            await this.service.resume(id);
            logger.debug("Recurring payment rule resumed successfully", { id });
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_UPDATE);
            throw error;
        }
    }
    /**
     * Cancel only the next scheduled payment
     * @param id - Rule ID (UUID)
     * @returns Success response
     */
    async cancelNext(id) {
        try {
            logger.debug("Cancelling next payment for recurring payment rule", { id });
            const result = await this.service.cancelNext(id);
            logger.debug("Next payment cancelled successfully", { id });
            return result;
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_CANCELLATION);
            throw error;
        }
    }
    /**
     * Update a recurring payment rule configuration
     * @param request - Update request with rule ID and fields to update
     * @returns Success response
     */
    async update(request) {
        try {
            logger.debug("Updating recurring payment rule", { id: request.id });
            const result = await this.service.update(request);
            logger.debug("Recurring payment rule updated successfully", { id: request.id });
            return result;
        }
        catch (error) {
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_UPDATE);
            throw error;
        }
    }
}
